<script setup lang="ts">
import { Notification, Notivue, NotivueSwipe } from 'notivue';
import { computed, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import BreakpointIndicator from '@/components/breakpoint-indicator.vue';
import WhatsNewModal from '@/components/doc-ai/doc-search/whats-new/whats-new-modal.vue';
import LumaFloating from '@/components/luma/luma-floating.vue';
import MaintenanceModal from '@/components/maintenance-modal.vue';
import OnboardingTour from '@/components/onboarding/onboarding-tour.vue';
import { useAppTitle } from '@/composables/use-app-title.ts';
import { useWhatsNew } from '@/composables/use-whats-new.ts';
import { useUserContext } from '@/composables/user/use-user-context.ts';
import { useUserProfile } from '@/composables/user/use-user-profile.ts';
import { useAuthStore } from '@/stores/auth-store.ts';

/* ---------------------------------------------------------------------------------------------- */
/*
 * Description: Vue app's bootstrap sfc
 */

useAppTitle('Home');

const route = useRoute();

const isDev = computed(() => import.meta.env.DEV);

/* ---------------------------------------------------------------------------------------------- */
/**
 * fetch user profile on every page refresh if the user is authenticated
 */

const authStore = useAuthStore();
const { fetchUserProfile } = useUserProfile();
const { watchAuthState } = useUserContext();

watch(() => authStore?.isAuthenticated, async (isAuthenticated) => {
   try {
      if (isAuthenticated) {
         await fetchUserProfile(true);
      }
   }
   catch (error) {
      console.error(error);
   }
}, { immediate: true });

// Sync user context (LaunchDarkly, Mixpanel) with user authentication state
watchAuthState();

/* ---------------------------------------------------------------------------------------------- */

// What's New popup - shows automatically on first visit after a new release
const { isOpen: isWhatsNewOpen, close: closeWhatsNew } = useWhatsNew();

/* ---------------------------------------------------------------------------------------------- */
</script>

<template>
   <!-- Maintenance modal (shows once per login) -->
   <MaintenanceModal />

   <!-- What's New Modal - shows automatically on first visit after a new release -->
   <WhatsNewModal
      v-if="authStore.isAuthenticated"
      v-model="isWhatsNewOpen"
      @update:model-value="(val) => !val && closeWhatsNew()"
   />

   <RouterView v-slot="{ Component }">
      <template v-if="Component">
         <Suspense>
            <!-- :key forces a remount on every route change so page components
                 don't have to manually sync to route param changes. -->
            <component :is="Component" :key="route.path"></component>

            <!-- loading state -->
            <template #fallback>
               Loading...
            </template>
         </Suspense>

         <!-- notivue toast -->
         <Notivue v-slot="item">
            <NotivueSwipe :item="item">
               <Notification :item="item" />
            </NotivueSwipe>
         </Notivue>
      </template>
   </RouterView>

   <!-- Global floating Luma assistant — reachable on every authenticated page -->
   <LumaFloating v-if="authStore.isAuthenticated" />

   <!-- First-run onboarding tour (auto-runs once, relaunchable from the user menu) -->
   <OnboardingTour v-if="authStore.isAuthenticated" />

   <BreakpointIndicator v-if="isDev" />
</template>

<style scoped>
</style>
